// Time-clock math. Pure functions only — no network, no store, no React — so
// the web app, the mobile app, and the tests all agree on what an hour is.
//
// The rule this file exists to enforce: CLOCKED time is not BILLED time. These
// helpers turn raw punches into a defensible number of hours, and a human
// decides whether that number goes on an invoice.

export const TIME_ENTRY_KINDS = {
  SHOP: 'SHOP',
  ROAD_PORTAL: 'ROAD_PORTAL',
};

export function nowSeconds() {
  return Math.floor(Date.now() / 1000);
}

// Timestamps in this app are seconds, but some legacy rows carry milliseconds.
function toSeconds(ts) {
  if (!ts) return null;
  return ts > 10_000_000_000 ? Math.floor(ts / 1000) : ts;
}

// Elapsed seconds for one entry. An open entry is measured to `asOf` so a
// running clock ticks in the UI without needing to be stopped.
export function entrySeconds(entry, asOf = nowSeconds()) {
  const start = toSeconds(entry?.startedAt);
  if (!start) return 0;
  const end = toSeconds(entry?.endedAt) ?? asOf;
  // A clock that ran backwards (device clock skew, a bad manual edit) is zero,
  // never negative — a negative hour would silently reduce an invoice.
  return Math.max(0, end - start);
}

export function isRunning(entry) {
  return Boolean(entry?.startedAt) && !entry?.endedAt;
}

export function totalSeconds(entries, asOf = nowSeconds()) {
  return (entries || []).reduce((sum, e) => sum + entrySeconds(e, asOf), 0);
}

// Hours as a raw decimal — for display of what actually elapsed.
export function secondsToHours(seconds) {
  return (Number(seconds) || 0) / 3600;
}

// Round to a billing increment. Shops bill to the quarter hour; `increment` is
// in hours so 0.25 = quarter, 0.1 = tenth, 0 = don't round.
//
// Rounds UP by default: a shop that has spent 1.05 hours bills 1.25, not 1.0.
// That matches how every shop quotes book time, and rounding down would be
// giving away labor on every single line.
export function roundHours(hours, increment = 0.25) {
  const h = Number(hours) || 0;
  const inc = Number(increment) || 0;
  if (h <= 0) return 0;
  if (inc <= 0) return Math.round(h * 100) / 100;
  return Math.round(Math.ceil(h / inc) * inc * 100) / 100;
}

// What actually goes on the invoice: rounded, then floored at the minimum.
// Returns the pieces so the UI can show WHY the number differs from the clock —
// a customer asking "you were here 40 minutes, why does this say 2 hours?"
// deserves an answer on the invoice, not an argument.
export function billableHours(rawHours, { increment = 0.25, minimumHours = 0 } = {}) {
  const actual  = Math.round((Number(rawHours) || 0) * 100) / 100;
  const rounded = roundHours(actual, increment);
  const min     = Number(minimumHours) || 0;
  const billed  = Math.max(rounded, min);
  return {
    actual,
    rounded,
    billed,
    minimumApplied: billed > rounded,
    roundedUp:      rounded > actual,
  };
}

// ── Grouping ─────────────────────────────────────────────────────────────────

// Clocked hours per tech on a job. Feeds the "who worked this and for how
// long" line the writer sees before billing.
export function hoursByTech(entries, asOf = nowSeconds()) {
  const map = new Map();
  for (const e of entries || []) {
    const key = e.techNumber || e.technicianId || e.techName || 'unknown';
    const prev = map.get(key) || { techNumber: e.techNumber || null, techName: e.techName || null, seconds: 0, running: false };
    prev.seconds += entrySeconds(e, asOf);
    prev.running = prev.running || isRunning(e);
    if (!prev.techName && e.techName) prev.techName = e.techName;
    map.set(key, prev);
  }
  return [...map.values()]
    .map(t => ({ ...t, hours: secondsToHours(t.seconds) }))
    .sort((a, b) => b.seconds - a.seconds);
}

// The number that makes the clock worth having: clocked vs already invoiced.
// `invoicedHours` comes from the estimate's labor lines.
export function clockedVsInvoiced(entries, invoicedHours, asOf = nowSeconds()) {
  const clocked = secondsToHours(totalSeconds(entries, asOf));
  const invoiced = Number(invoicedHours) || 0;
  return {
    clocked:  Math.round(clocked * 100) / 100,
    invoiced: Math.round(invoiced * 100) / 100,
    gap:      Math.round((clocked - invoiced) * 100) / 100, // positive = unbilled labor
  };
}

// Entries a writer hasn't turned into a labor line yet.
export function unbilledEntries(entries) {
  return (entries || []).filter(e => !e.billedAt && !isRunning(e));
}
